import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {AppearanceSettingsInterface} from '../../api/interfaces/appearance-settings.interface';

/** Stage both stylesheets before replacing either one. Only the latest request may commit. */
@Injectable({providedIn: 'root'})
export class AppearanceStylesheetService implements OnDestroy {
  private cancelPending?: () => void;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  public cancel(): void { this.cancelPending?.(); }

  public load(settings: AppearanceSettingsInterface): Promise<boolean> {
    this.cancel();
    const targets = [
      {id: 'layout-css', href: `assets/layout/css/layout-${settings.layoutMode}.css`},
      {id: 'theme-css', href: `assets/theme/${settings.theme}/theme-${settings.layoutMode}.css`},
    ];
    return new Promise((resolve, reject) => {
      const pending: Array<{original: HTMLLinkElement; staged: HTMLLinkElement}> = [];
      let remaining = 0;
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const cleanup = () => {
        clearTimeout(timer);
        pending.forEach(({staged}) => { staged.onload = null; staged.onerror = null; });
        if (this.cancelPending === cancel) { this.cancelPending = undefined; }
      };
      const cancel = () => {
        if (settled) { return; }
        settled = true;
        cleanup();
        pending.forEach(({staged}) => staged.remove());
        resolve(false);
      };
      const fail = () => {
        if (settled) { return; }
        settled = true;
        cleanup();
        pending.forEach(({staged}) => staged.remove());
        reject(new Error('Appearance stylesheet unavailable'));
      };
      const commit = () => {
        if (settled || remaining > 0) { return; }
        settled = true;
        cleanup();
        pending.forEach(({original, staged}) => {
          staged.media = 'all';
          staged.id = original.id;
          original.remove();
        });
        resolve(true);
      };
      this.cancelPending = cancel;
      for (const {id, href} of targets) {
        const original = this.document.getElementById(id) as HTMLLinkElement | null;
        if (!original) { fail(); return; }
        if (original.getAttribute('href') === href) { continue; }
        const staged = original.cloneNode(false) as HTMLLinkElement;
        staged.id = `${id}-pending`;
        staged.media = 'not all';
        staged.href = href;
        pending.push({original, staged});
      }
      remaining = pending.length;
      if (!remaining) { commit(); return; }
      timer = setTimeout(fail, 15000);
      pending.forEach(({original, staged}) => {
        staged.onload = () => { remaining--; commit(); };
        staged.onerror = fail;
        original.parentNode!.insertBefore(staged, original.nextSibling);
      });
    });
  }

  public ngOnDestroy(): void { this.cancel(); }
}
